import {
  CLOSE_PATIENT,
  LOAD_ADDRESS_SUCCESS,
  LOAD_PATIENT_START,
  LOAD_PATIENT_SUCCESS,
  PatientAction,
  SET_CO_PAYMENT,
  SET_EFACT_EATTEST,
  SET_NOMENCLATURE_CODE,
  SET_NUMBER_OF_PATIENTS,
  SET_PATIENT_TYPE,
  SET_PAYED_AMOUNT,
  SET_PAYMENT_METHOD,
  TARIFIED_TODAY,
} from "store/types/patient.types";
import { GlobalState } from "store/configure-store";
import {
  DELETE_TARIFIED_ITEM_SUCCESS,
  FINISH_TARIFICATION_SESSION_ERROR,
  FINISH_TARIFICATION_SESSION_START,
  FINISH_TARIFICATION_SESSION_SUCCESS,
  LOAD_INSURABILITY_SUCCESS,
  TARIFICATION_ETAR_ERROR,
  TARIFICATION_ETAR_START,
  TARIFICATION_ETAR_SUCCESS,
  TARIFICATION_SESSION_DETAILS_SUCCESS,
  TARIFICATION_SESSION_START,
  TARIFICATION_SESSION_SUCCESS,
  TarificationAction,
  UPDATE_TARIFIED_ITEM_START,
  UPDATE_TARIFIED_ITEM_SUCCESS,
} from "store/types/tarification.types";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import { PatientTypeEnum } from "common/enums/PatientTypeEnum";
import { EFactEAttestEnum } from "common/enums/EFactEAttestEnum";
import { CoPaymentEnum } from "common/enums/CoPaymentEnum";
import {
  Attestation,
  AttestationProgress,
  AttestationType,
} from "model/attestion.model";
import { ETarResult } from "model/e-tar.result";
import { PaymentMethodEnum } from "common/enums/PaymentMethodEnum";
import { Patient } from "model/patient.model";
import { TimelineItem } from "common/models/TimelineItem";
import { Address } from "common/models/Address";
import { Nomenclature } from "common/models/Nomenclature";
import { Insurability } from "model/insurability.model";
import {
  InsurabilityAction,
  LOAD_CHRONIC_INFORMATION,
} from "store/types/insurability.types";
import { Contact, ContactType } from "model/contact.model";
import {
  CONTACT_CREATED,
  CONTACT_UPDATE,
  ContactAction,
} from "store/types/contact.types";
import {
  ATTESTATION_HELENA_ERROR,
  ATTESTATION_HELENA_START,
  HelenaAction,
} from "store/types/helena.types";
import {
  SUB_CONTACT_CREATED,
  SubContactAction,
} from "store/types/sub-contact.types";
import {
  SubContactItem,
  SubContactItemType,
} from "common/models/SubContactItem";
import { PatientSync, SyncServiceType } from "model/patient-sync.model";
import {
  LOAD_PATIENTSYNC_SUCCESS,
  PatientSyncAction,
  SET_PATIENTSYNC_SUCCESS,
} from "store/types/patient-sync.types";

export interface PatientState {
  patient: Patient;
  patientSync: Array<PatientSync>;
  tarificationSession?: TarificationSessionKey;
  eTarResult?: ETarResult;
  attestation: AttestationType;
  listOverview: Array<TimelineItem>;
  contact: ContactType;
  subContact: SubContactItemType;
  tarifiedToday: boolean;
  insurability: Insurability;
}

export const initialPatientContextState: PatientState = {
  patient: new Patient(),
  patientSync: [],
  tarificationSession: undefined,
  eTarResult: undefined,
  attestation: new Attestation(),
  listOverview: [],
  contact: new Contact(),
  subContact: new SubContactItem(),
  tarifiedToday: false,
  insurability: new Insurability(),
};

/** ---- SELECTORS ---- */
export const selectCurrentContact = (state: GlobalState): ContactType => {
  return state.patientContext.contact;
};

export const selectCurrentSubContact = (state: GlobalState): SubContactItem => {
  return new SubContactItem(state.patientContext.subContact);
};

export const selectCt1 = (state: GlobalState): string => {
  return state.patientContext.insurability.ct1;
};

export const selectCt2 = (state: GlobalState): string => {
  return state.patientContext.insurability.ct2;
};

export const selectCoPayment = (state: GlobalState): CoPaymentEnum => {
  if (state.patientContext.attestation.coPayment) {
    return CoPaymentEnum.CHARGED;
  } else {
    return CoPaymentEnum.NOT_CHARGED;
  }
};

export const selectTarifiedToday = (state: GlobalState): boolean => {
  return state.patientContext.tarifiedToday;
};

export const selectPaymentMethod = (state: GlobalState): PaymentMethodEnum => {
  return (
    state.patientContext.attestation.paymentMethod || PaymentMethodEnum.CASH
  );
};

export const selectPayedAmount = (state: GlobalState): number => {
  const payedAmount = state.patientContext.attestation.payedAmount;
  if (payedAmount || payedAmount === 0) {
    // 0 is valid too
    return payedAmount;
  }
  return selectDefaultPayedAmount(state);
};

export const selectDefaultPayedAmount = (state: GlobalState): number => {
  if (state.patientContext.tarificationSession) {
    if (selectEFactEAttest(state) === EFactEAttestEnum.EFact) {
      return state.patientContext.tarificationSession.totalPatientContribution;
    } else {
      return state.patientContext.tarificationSession.totalValue;
    }
  }
  return 0;
};

export const selectPatient = (state: GlobalState): Patient => {
  return new Patient(state.patientContext.patient);
};

export const selectAttestationRequest = (state: GlobalState): Attestation => {
  return new Attestation(state.patientContext.attestation);
};
export const selectNumberOfPatients = (state: GlobalState): number => {
  return state.patientContext.attestation.numberOfPatients;
};

export const selectPatientType = (state: GlobalState): PatientTypeEnum => {
  if (state.patientContext.attestation.palliative) {
    return PatientTypeEnum.PALLIATIVE;
  } else if (state.patientContext.attestation.chronic) {
    return PatientTypeEnum.CHRONIC;
  } else {
    return PatientTypeEnum.NONE;
  }
};

export const selectIsEAttest = (state: GlobalState): boolean => {
  return !state.patientContext.attestation.eFact;
};

export const selectEFactEAttest = (state: GlobalState): EFactEAttestEnum => {
  if (state.patientContext.attestation.eFact) {
    return EFactEAttestEnum.EFact;
  } else if (state.patientContext.attestation.eAttest) {
    return EFactEAttestEnum.EAttest;
  } else {
    return EFactEAttestEnum.EAttest;
  }
};

export const selectEtarResult = (
  state: GlobalState
): ETarResult | undefined => {
  return state.patientContext.eTarResult;
};

export const selectTarificationSessionForPatient = (
  state: GlobalState
): TarificationSessionKey | undefined => {
  return state.patientContext.tarificationSession;
};

export const selectTarificationFinishedResult = (
  state: GlobalState
): AttestationProgress => {
  return state.patientContext.attestation.attestationProgress;
};

export const selectPatientSync = (
  state: GlobalState,
  syncServiceType: SyncServiceType
): PatientSync | undefined => {
  return state.patientContext.patientSync.find(
    (sync) => sync.syncServiceType === syncServiceType
  );
};

/** ---- ACTION HANDLERS ---- */
const setPayedAmount = (state: PatientState, payedAmount: number) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      payedAmount: payedAmount,
    }),
  };
};

const setPaymentMethod = (
  state: PatientState,
  paymentMethod: PaymentMethodEnum
) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      paymentMethod: paymentMethod,
    }),
  };
};

const tarificationSessionStart = (state: PatientState) => {
  return {
    ...state,
    tarificationSession: undefined,
    eTarResult: undefined,
    attestation: Object.assign({}, state.attestation, { amount: undefined }),
  };
};

const tarificationEtarStart = (state: PatientState) => {
  return { ...state, eTarResult: undefined };
};

const tarificationEtarSuccess = (
  state: PatientState,
  eTarResult: ETarResult
) => {
  return { ...state, eTarResult: eTarResult };
};

const tarificationEtarError = (state: PatientState) => {
  return {
    ...state,
    eTarResult: new ETarResult({
      brokenRules: [],
      error: "Something went wrong",
    }),
  };
};

const finishTarificationSessionStart = (state: PatientState) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      attestationProgress: AttestationProgress.START_TARIFICATION,
    }),
  };
};

const finishTarificationSessionSuccess = (state: PatientState) => {
  return {
    ...state,
    tarificationSession: undefined,
    attestation: Object.assign({}, state.attestation, {
      attestationProgress: AttestationProgress.DONE,
      dirty: true,
    }),
  };
};

const finishTarificationSessionError = (state: PatientState) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      attestationProgress: AttestationProgress.ERROR_TARIFICATION,
      dirty: true,
    }),
  };
};

const attestationHelenaStart = (state: PatientState) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      attestationProgress: AttestationProgress.START_HELENA,
    }),
  };
};

const attestationHelenaError = (state: PatientState) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      attestationProgress: AttestationProgress.ERROR_HELENA,
    }),
  };
};

const resetPatientContext = () => {
  return {
    ...initialPatientContextState,
  };
};

const loadPatientSuccess = (state: PatientState, patient: Patient) => {
  return {
    ...state,
    patient: patient,
  };
};

const loadAddressSuccess = (state: PatientState, address: Address) => {
  return {
    ...state,
    patient: Object.assign({}, state.patient, {
      address: address,
    }),
  };
};

const loadPatientSyncSuccess = (
  state: PatientState,
  patientSync: Array<PatientSync>
) => {
  return {
    ...state,
    patientSync: patientSync,
  };
};

const setPatientSyncSuccess = (
  state: PatientState,
  patientSync: PatientSync
) => {
  return {
    ...state,
    patientSync: [...state.patientSync, patientSync],
  };
};

const tarificationSessionSuccess = (
  state: PatientState,
  tarificationSession: TarificationSessionKey
) => {
  return {
    ...state,
    tarificationSession: tarificationSession,
    attestation: Object.assign({}, state.attestation, {
      dirty: false,
      attestationProgress: AttestationProgress.NEW,
    }),
    eTarResult: undefined,
  };
};

const deleteTarifiedItemSuccess = (
  state: PatientState,
  tarifiedItemId: string
) => {
  return {
    ...state,
    tarificationSession: Object.assign({}, state.tarificationSession, {
      tarifiedItems: state.tarificationSession
        ? state.tarificationSession.tarifiedItems.filter(
            (it) => it.id !== tarifiedItemId
          )
        : [],
    }),
    attestation: Object.assign({}, state.attestation, { dirty: true }),
  };
};

const setNumberOfPatients = (state: PatientState, numberOfPatients: number) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      numberOfPatients: numberOfPatients,
      dirty: true,
    }),
  };
};

const setNomenclatureCode = (
  state: PatientState,
  nomenclatures: Array<Nomenclature>
) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      nomenclatures: nomenclatures,
      dirty: true,
    }),
  };
};

const setPatientType = (state: PatientState, patientType: PatientTypeEnum) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      chronic: patientType === PatientTypeEnum.CHRONIC,
      palliative: patientType === PatientTypeEnum.PALLIATIVE,
      dirty: true,
    }),
  };
};

const setEFactEAttest = (
  state: PatientState,
  eFactEAttest: EFactEAttestEnum
) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      eFact: eFactEAttest === EFactEAttestEnum.EFact,
      eAttest: eFactEAttest === EFactEAttestEnum.EAttest,
      dirty: true,
    }),
  };
};

const loadInsurabilitySuccess = (
  state: PatientState,
  payload: Insurability
) => {
  const defaultEFact =
    !!payload.ct1 &&
    payload.ct1.endsWith("1") &&
    !!payload.ct2 &&
    payload.ct2.endsWith("1");
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      eFact: defaultEFact,
      eAttest: !defaultEFact,
    }),
    insurability: payload,
  };
};

const setCoPayment = (state: PatientState, coPayment: CoPaymentEnum) => {
  return {
    ...state,
    attestation: Object.assign({}, state.attestation, {
      coPayment: coPayment === CoPaymentEnum.CHARGED,
      dirty: true,
    }),
  };
};

const setChronicInformation = (state: PatientState, chronic: boolean) => {
  return {
    ...state,
    attestation: { ...state.attestation, chronic: chronic },
  };
};

const tarifiedToday = (state: PatientState, tarifiedToday: boolean) => {
  return { ...state, tarifiedToday: tarifiedToday };
};

const contactCreated = (state: PatientState, contact: ContactType) => {
  return { ...state, contact: contact };
};

const subContactCreated = (
  state: PatientState,
  subContact: SubContactItemType
) => {
  return { ...state, subContact: subContact };
};

/** ---- REDUCER ---- */
export const patientReducer = (
  state = initialPatientContextState,
  action:
    | PatientAction
    | TarificationAction
    | InsurabilityAction
    | ContactAction
    | SubContactAction
    | HelenaAction
    | PatientSyncAction
): PatientState => {
  switch (action.type) {
    case CONTACT_CREATED:
    case CONTACT_UPDATE:
      return contactCreated(state, action.payload);
    case SUB_CONTACT_CREATED:
      return subContactCreated(state, action.payload);
    case SET_PAYED_AMOUNT:
      return setPayedAmount(state, action.payload);
    case SET_PAYMENT_METHOD:
      return setPaymentMethod(state, action.payload);
    case TARIFICATION_SESSION_START:
    case UPDATE_TARIFIED_ITEM_START:
      return tarificationSessionStart(state);
    case TARIFICATION_ETAR_START:
      return tarificationEtarStart(state);
    case TARIFICATION_ETAR_SUCCESS:
      return tarificationEtarSuccess(state, action.payload);
    case TARIFICATION_ETAR_ERROR:
      return tarificationEtarError(state);
    case FINISH_TARIFICATION_SESSION_START:
      return finishTarificationSessionStart(state);
    case FINISH_TARIFICATION_SESSION_SUCCESS:
      return finishTarificationSessionSuccess(state);
    case FINISH_TARIFICATION_SESSION_ERROR:
      return finishTarificationSessionError(state);
    case ATTESTATION_HELENA_START:
      return attestationHelenaStart(state);
    case ATTESTATION_HELENA_ERROR:
      return attestationHelenaError(state);
    case CLOSE_PATIENT:
    case LOAD_PATIENT_START:
      return resetPatientContext();
    case LOAD_PATIENT_SUCCESS:
      return loadPatientSuccess(state, action.payload);
    case LOAD_ADDRESS_SUCCESS:
      return loadAddressSuccess(state, action.payload);
    case LOAD_PATIENTSYNC_SUCCESS:
      return loadPatientSyncSuccess(state, action.payload);
    case SET_PATIENTSYNC_SUCCESS:
      return setPatientSyncSuccess(state, action.payload);
    case TARIFICATION_SESSION_SUCCESS:
    case TARIFICATION_SESSION_DETAILS_SUCCESS:
    case UPDATE_TARIFIED_ITEM_SUCCESS:
      return tarificationSessionSuccess(state, action.payload);
    case DELETE_TARIFIED_ITEM_SUCCESS:
      return deleteTarifiedItemSuccess(state, action.payload);
    case SET_NUMBER_OF_PATIENTS:
      return setNumberOfPatients(state, action.payload);
    case SET_NOMENCLATURE_CODE:
      return setNomenclatureCode(state, action.payload);
    case SET_PATIENT_TYPE:
      return setPatientType(state, action.payload);
    case SET_EFACT_EATTEST:
      return setEFactEAttest(state, action.payload);
    case LOAD_INSURABILITY_SUCCESS:
      return loadInsurabilitySuccess(state, action.payload);
    case SET_CO_PAYMENT:
      return setCoPayment(state, action.payload);
    case LOAD_CHRONIC_INFORMATION:
      return setChronicInformation(state, action.payload);
    case TARIFIED_TODAY:
      return tarifiedToday(state, action.payload);
    default:
      return state;
  }
};
